import { Fragment, useEffect } from 'react';
import { Grid, Link, styled, Typography, Box } from '@mui/material';
import PropTypes from 'prop-types';
import './ExpandedView.scss';
import Area from 'components/Area';
import Currency from 'components/Currency';
import Distance from 'components/Distance';
import FormattedDate from 'components/FormattedDate';
import Optional from 'components/Optional';
import ReadMoreModal from 'components/ReadMoreModal';
import AnalyticsGateway from 'lib/AnalyticsGateway';
import Formatter from 'lib/Formatter';
import LoadingPanel from 'components/LoadingPanel/LoadingPanel';
import { PropertyImageGalleryContainer } from 'components/PropertyImageGallery';
import { presetDimensions } from 'components/imaging/ResizedDasWebImage';
import { comparableTypes } from 'lib/PropertyUtils';
import PlaceholderImage from 'images/no-image@2x.png';
import S3Util from 'lib/S3Util';

const GridLabelContainer = styled(Grid)(({theme}) => ({
    color: theme?.palette?.tertiary?.title,
}));

const StyleImage = styled('img')({
    maxWidth: '18px',
    marginRight: '3px',
});

const ExpandedView = ({
    advertisementDescription,
    comparableType,
    isNZ,
    featureFlags,
    property,
    pageContext,
    settings,
    updateIndex,
    onDelete,
    onFetchAdvertisementDescription,
    onUpload
}) => {
    useEffect(() => {
        if (!advertisementDescription && featureFlags.rapidSearchEnabled) {
            onFetchAdvertisementDescription();
        }
    }, [
        advertisementDescription,
        featureFlags.rapidSearchEnabled,
        isNZ,
        onFetchAdvertisementDescription
    ]);

    const dispatchMixpanelEvent = () => {
        AnalyticsGateway.sendEvent({
            eventName: 'View Property in RPP',
            properties: [
                {label: 'Page', value: pageContext},
                {label: 'Property ID', value: property.propertyId}
            ]
        });
    };

    const renderAdvertisementDescription = () => {
        if (property.fetching?.advertisementDescription) {
            return (
                <Grid item size={12}>
                    <LoadingPanel isLoading={true}/>
                </Grid>
            );
        }

        return (
            <>
                <Grid item size={12}>
                    {advertisementDescription || 'No listing description available.'}
                </Grid>
                <Grid item size={12}>
                    {getReadMoreModal()}
                </Grid>
            </>
        );
    };

    const getReadMoreModal = () => {
        const hasReadMore = advertisementDescription && advertisementDescription.endsWith('...');
        if (hasReadMore)
            return <ReadMoreModal description={property.advertisementDescription}/>;
        return null;
    };

  const getBackLink = () => {
    if (settings.rpd) {
      return (
        <Link
            className="back-link"
            href={`${settings.rpd.baseUrl}/property/${property.propertyId}`}
            onClick={dispatchMixpanelEvent}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
            }}
        >
          <StyleImage alt="back link logo"
              src={S3Util.getAssetDomain('/logos/cotality-icon-dark.png')}
          />
            <Typography color="primary">
                {`Go to ${isNZ ? 'Property Guru' : 'RP Data'}`}
            </Typography>
        </Link>
      );
    }
    return null;
  };

    return (
        <div className={`ExpandedView ${isNZ ? 'NZ' : 'AU'} body`}>
            <PropertyImageGalleryContainer
                comparableType={comparableType}
                key={`expanded-${comparableType}-property-gallery-${property.propertyId}`}
                isComparable
                currentIndex={property.propertyPhotoUriSelectedIndex || 0}
                deletableImages={property.uploadPhotoList}
                images={property.propertyPhotoList || [PlaceholderImage]}
                inlineGalleryProps={{
                    className: 'media inline-gallery',
                    onIndexChange: updateIndex('Inline')
                }}
                objectContext={pageContext}
                pageContext={pageContext}
                propertyId={property.propertyId}
                resizeProps={{
                    isResize: true,
                    resizeDimensions: presetDimensions.PROPERTY_MEDIUM
                }}
                onDelete={() => onDelete(property.propertyPhotoUriSelectedIndex || 0)}
                onIndexChange={updateIndex('Gallery')}
                onUpload={onUpload}
            />
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Grid container p={1.25} size={12} sx={{ minHeight: '60px' }}>
                    {renderAdvertisementDescription()}
                </Grid>
                <Grid container spacing={1} p={1.25} sx={{ flexGrow: 1 }}>
                    <Grid container item size={12}>
                        <Grid
                            container
                            item
                            size={isNZ ? 7 : 8}
                            spacing={1}
                            sx={{
                                borderRight: '1px solid #dadada',
                            }}>
                            <Grid container item size={12}>
                                {pageContext !== 'Comparable Rentals' && (
                                    <>
                                        <GridLabelContainer item size={3}>
                                            First Listed Price
                                        </GridLabelContainer>
                                        <Grid item size={9}>
                                            <Typography title={property.firstPublishedPrice} variant="fontWeight600">
                                                {Formatter.listingPrice(property.firstPublishedPrice)}
                                            </Typography>
                                        </Grid>
                                    </>

                                )}
                                {pageContext !== 'Comparable Rentals' && (
                                    <>
                                        <GridLabelContainer item size={3}>
                                            Last Listed Price
                                        </GridLabelContainer>
                                        <Grid item size={9}>
                                            <Typography title={property.latestPublishedPrice} variant="fontWeight600">
                                                {Formatter.listingPrice(property.latestPublishedPrice)}
                                            </Typography>
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                        </Grid>
                        <Grid container item size={isNZ ? 5 : 4} spacing={0.5}>
                            <Grid container item size={isNZ ? 5 : 6}>
                                <GridLabelContainer item size={6}>
                                    Floor
                                </GridLabelContainer>
                                <Grid item size={6}>
                                    <Area area={property.floorArea}/>
                                </Grid>
                            </Grid>
                            {!isNZ && (
                                <Grid container item size={6}>
                                    <GridLabelContainer item size={7}>
                                        Year built
                                    </GridLabelContainer>
                                    <Grid item size={5}>
                                        <Optional value={property.yearBuilt}/>
                                    </Grid>
                                </Grid>
                            )}
                            {isNZ && (
                                <Grid container item size={7}>
                                    <GridLabelContainer item size={6}>
                                        Capital value
                                    </GridLabelContainer>
                                    <Grid item size={6}>
                                        <Currency currency={property.capitalValue}/>
                                    </Grid>
                                </Grid>
                            )}
                            <Grid container item size={isNZ ? 5 : 6}>
                                <GridLabelContainer item size={6}>
                                    Distance
                                </GridLabelContainer>
                                <Grid item size={6}>
                                    <Distance distance={property.distance}/>
                                </Grid>
                            </Grid>
                            {isNZ && (
                                <Fragment>
                                    <Grid container item size={7}>
                                        <GridLabelContainer item size={6}>
                                            CV date
                                        </GridLabelContainer>
                                        <Grid item size={6}>
                                            <FormattedDate date={property.capitalValueDate}/>
                                        </Grid>
                                    </Grid>
                                    <Grid container item size={5}>
                                        <GridLabelContainer item size={6}>
                                            Age
                                        </GridLabelContainer>
                                        <Grid item size={6}>
                                            <Optional value={property.yearBuilt}/>
                                        </Grid>
                                    </Grid>
                                </Fragment>
                            )}
                            <Grid container item size={isNZ ? 7 : 6}>
                                {getBackLink()}
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
        </div>
    );
};

ExpandedView.propTypes = {
    advertisementDescription: PropTypes.string,
    comparableType: PropTypes.oneOf(comparableTypes),
    isNZ: PropTypes.bool,
    featureFlags: PropTypes.shape({
        rapidSearchEnabled: PropTypes.bool,
    }),
    property: PropTypes.shape({
        propertyId: PropTypes.number.isRequired,
        advertisementDescription: PropTypes.string,
        singleLineAddress: PropTypes.string.isRequired,
        bedrooms: PropTypes.number,
        bathrooms: PropTypes.number,
        capitalValue: PropTypes.number,
        capitalValueDate: PropTypes.string,
        carSpaces: PropTypes.number,
        daysOnMarket: PropTypes.number,
        distance: PropTypes.number,
        floorArea: PropTypes.number,
        landArea: PropTypes.number,
        yearBuilt: PropTypes.number,
        saleDate: PropTypes.string,
        salePrice: PropTypes.number,
        firstPublishedPrice: PropTypes.string,
        latestPublishedPrice: PropTypes.string,
        propertyPhotoList: PropTypes.arrayOf(PropTypes.string.isRequired),
        uploadPhotoList: PropTypes.arrayOf(PropTypes.string.isRequired),
        propertyPhotoUriSelectedIndex: PropTypes.number,
        fetching: PropTypes.object,
    }).isRequired,
    pageContext: PropTypes.string,
    settings: PropTypes.shape({
        rpd: PropTypes.shape({
            baseUrl: PropTypes.string,
        })
    }),
    updateIndex: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    onFetchAdvertisementDescription: PropTypes.func.isRequired,
    onUpload: PropTypes.func.isRequired,
};

ExpandedView.defaultProps = {
    isNZ: false,
    propertyDetailsSpacing: 6
};

export default ExpandedView;
